'use client';

import { useState, useMemo } from 'react';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { ArrowDown01Icon, ArrowRight01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { Text } from '../../../components/styled/text';
import { Collapsible } from '../../../components/collapsible';
import { type TraceToolCall } from '../../chat/trace-view/types';
import { formatDurationBySeconds } from '../../chat/trace-view/utils';
import { useArtifactsOptional } from '../../chat/trace-view/ArtifactsProvider';
import { AddToEvidenceButton } from '../components/AddToEvidenceButton';

type LogEntry = {
  timestamp: string;
  tool: string;
  value: string;
};
type LogArtifactProps = {
  toolCall: TraceToolCall;
  displayName: string;
};

function parseLogEntries(output: string): LogEntry[] {
  try {
    const parsed = JSON.parse(output) as unknown;

    if (!Array.isArray(parsed)) return [];

    return parsed.filter(
      (entry): entry is LogEntry =>
        typeof entry === 'object' &&
        entry !== null &&
        typeof entry.timestamp === 'string' &&
        typeof entry.tool === 'string' &&
        typeof entry.value === 'string',
    );
  } catch {
    return [];
  }
}

function formatLogTimestamp(iso: string): string {
  try {
    const date = new Date(iso);
    const h = String(date.getHours()).padStart(2, '0');
    const m = String(date.getMinutes()).padStart(2, '0');
    const s = String(date.getSeconds()).padStart(2, '0');
    const ms = String(date.getMilliseconds()).padStart(3, '0');

    return `${h}:${m}:${s}.${ms}`;
  } catch {
    return iso;
  }
}

function calculateElapsedMs(entries: LogEntry[]): number {
  if (entries.length < 2) return 0;
  const first = new Date(entries[0].timestamp).getTime();
  const last = new Date(entries[entries.length - 1].timestamp).getTime();

  return last - first;
}

type LogTimelineProps = {
  entries: LogEntry[];
};

function LogTimeline({ entries }: LogTimelineProps) {
  const elapsed = useMemo(() => calculateElapsedMs(entries), [entries]);

  return (
    <Box
      borderWidth="1px"
      borderColor="border.neutral.muted"
      borderRadius="md"
      bg="transparent"
      overflow="hidden"
      width="100%"
    >
      <HStack
        justify="space-between"
        alignItems="center"
        px={3}
        h="36px"
        bg="transparent"
        borderBottomWidth="1px"
        borderBottomColor="border.neutral.muted"
      >
        <Text fontSize="xs" fontWeight="medium">
          {entries.length} {entries.length === 1 ? 'log entry' : 'log entries'}
        </Text>
        {elapsed > 0 && (
          <Text fontSize="xs" color="fg.muted">
            {formatDurationBySeconds(elapsed)}
          </Text>
        )}
      </HStack>

      <VStack
        gap={0}
        alignItems="stretch"
        width="100%"
        maxH="400px"
        overflow="auto"
      >
        {entries.map((entry, index) => (
          <HStack
            key={index}
            px={3}
            py={1}
            gap={3}
            alignItems="baseline"
            borderTopWidth={index > 0 ? '1px' : '0'}
            borderTopColor="border.neutral.muted"
            width="100%"
            _hover={{ bg: 'bg.neutral.muted' }}
          >
            <Text
              fontSize="xs"
              fontFamily="mono"
              color="fg.muted"
              flexShrink={0}
              w="90px"
            >
              {formatLogTimestamp(entry.timestamp)}
            </Text>
            <Text
              fontSize="xs"
              fontFamily="mono"
              fontWeight="medium"
              flexShrink={0}
              w="140px"
              truncate
              title={entry.tool}
            >
              {entry.tool}
            </Text>
            <Text
              fontSize="xs"
              fontFamily="mono"
              color="fg.default"
              flex="1"
              truncate
              title={entry.value}
            >
              {entry.value}
            </Text>
          </HStack>
        ))}
      </VStack>
    </Box>
  );
}

export function LogArtifact({ toolCall, displayName }: LogArtifactProps) {
  const t = useTranslations('designSystem.artifacts');
  const [isOpen, setIsOpen] = useState(true);
  const artifactsContext = useArtifactsOptional();
  const artifact = artifactsContext?.getArtifactByToolCallId(toolCall.id);
  const entries = useMemo(
    () => parseLogEntries(toolCall.output ?? ''),
    [toolCall.output],
  );
  const isCompleted = toolCall.status === 'completed';
  const hasResult = entries.length > 0;
  const showShimmer = !hasResult && !isCompleted;
  const shimmerStyle = showShimmer
    ? {
        background:
          'linear-gradient(90deg, var(--colors-fg-muted) 25%, var(--colors-fg-subtle) 50%, var(--colors-fg-muted) 75%)',
        backgroundSize: '200% 100%',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        animationTimingFunction: 'linear',
      }
    : undefined;
  const toolNameText = (
    <Text
      fontSize="xs"
      color="fg.muted"
      animationStyle={showShimmer ? 'shimmer' : undefined}
      style={shimmerStyle}
    >
      {displayName}
    </Text>
  );

  if (!hasResult && !isCompleted) {
    return (
      <HStack w="full" gap={1.5} alignItems="center" py={1} borderRadius="sm">
        {toolNameText}
      </HStack>
    );
  }

  if (!hasResult && isCompleted) {
    return (
      <HStack w="full" gap={1.5} alignItems="center" py={1} borderRadius="sm">
        <Text fontSize="xs" color="fg.muted">
          {displayName}
        </Text>
        <Text fontSize="xs" color="fg.muted">
          •
        </Text>
        <Text fontSize="xs" color="fg.muted">
          {t('noLogsCaptured')}
        </Text>
      </HStack>
    );
  }

  return (
    <Collapsible.Root
      open={isOpen}
      onOpenChange={({ open }) => setIsOpen(open)}
      lazyMount
      unmountOnExit
    >
      <Collapsible.Trigger asChild>
        <HStack
          w="full"
          gap={1.5}
          alignItems="center"
          py={1}
          cursor="pointer"
          borderRadius="sm"
        >
          {toolNameText}

          <Box display="flex" alignItems="center" color="fg.muted">
            {isOpen ? (
              <ArrowDown01Icon size={12} />
            ) : (
              <ArrowRight01Icon size={12} />
            )}
          </Box>

          <Box flex={1} />

          {isCompleted && (
            <AddToEvidenceButton
              artifactId={artifact?.id ?? artifact?.runtimeId ?? undefined}
              isEvidence={artifactsContext?.isEvidence(artifact) ?? false}
            />
          )}
        </HStack>
      </Collapsible.Trigger>

      <Collapsible.Content>
        <VStack alignItems="stretch" gap={2} width="100%" py={2}>
          <LogTimeline entries={entries} />
        </VStack>
      </Collapsible.Content>
    </Collapsible.Root>
  );
}
